import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { get } from 'lodash';
import { Dropdown, Space, Button, Pagination } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { ManualAlert, ManualAlertEvent } from '@conviva/portal-graphql-schema';
import { useQuery } from '@apollo/react-hooks';
import { Loading } from '@components/common';
import { isAdminRoleSelector } from '@store/common/selectors';
import { EXPORT_ALERT_EVENTS } from '@store/export/types';
import { ALERT_EVENTS_QUERY } from '@api/graphql/manualAlerts';
import Table from '@components/common/Table';
import { useLocationQuery } from '@hooks';
import columns from './columns';
import { Title, PageHeader, TableWrapper, PageButtons, Wrapper } from './styled';
import { getMetricInfo } from '../constants';
import { TableData } from '../summary/components/AlertTable/utils/formatTableData';
import AlertForm from '../form';
import { FormActionContext } from '../form/context';
import { Confirmation, convertValue } from '../shared';

const pageSize = 500;
const Events = ({ history }) => {
  const query = useLocationQuery();
  const dispatch = useDispatch();
  const alertId = parseInt(query.get('alertId'), 10);
  const page = parseInt(query.get('page'), 10) || 1;
  const { loading, data } = useQuery(ALERT_EVENTS_QUERY, {
    variables: { ids: [alertId], start: (page - 1) * pageSize, count: pageSize },
  });
  const alert = get(data, 'manualAlerts.0', null);
  const [visible, setVisible] = useState<boolean>(false);
  const [editAlert, setEditAlert] = useState(alert);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<{} | ManualAlert>({});
  const isAdmin = useSelector(isAdminRoleSelector);
  const onCancel = useCallback(() => {
    setVisible(false);
  }, [setVisible]);
  const onClick = useCallback(() => {
    setEditAlert(alert);
    setVisible(true);
  }, [setVisible, alert, setEditAlert]);
  const onClickDropDown = useCallback(
    (e) => {
      switch (e) {
        case 'export':
          dispatch({
            type: EXPORT_ALERT_EVENTS,
            alertIds: [alert.id],
          });
          break;
      }
    },
    [alert, dispatch],
  );

  const onCancelConfirmation = useCallback(() => {
    setAlertToDelete({});
    setConfirmationVisible(false);
  }, [setAlertToDelete, setConfirmationVisible]);

  const onDelete = useCallback(
    (alert: ManualAlert) => {
      setConfirmationVisible(true);
      setAlertToDelete(alert);
    },
    [setConfirmationVisible, setAlertToDelete],
  );

  const onEdit = useCallback(
    (alert: ManualAlert) => {
      setVisible(true);
      setEditAlert(alert);
    },
    [setVisible, setEditAlert],
  );

  const onClickPage = useCallback(
    (p) => {
      history.push(`${window.location.pathname}?alertId=${alertId}&page=${p + 1}`);
    },
    [history],
  );

  if (loading) {
    return <Loading />;
  }

  if (!alert) {
    return null;
  }

  const { name, metric, direction, threshold, filter, samples } = alert;
  const metricsInfo = getMetricInfo();
  const metricInfo = metricsInfo[metric];
  const condition = `${metricInfo.name} ${direction} ${convertValue(threshold, metricInfo.units)} ${metricInfo.units}`;

  const alertData: TableData[] = [
    {
      name,
      filter,
      id: `alert-${alert.id}`,
      condition,
      sample: `${samples} ${metricInfo.cm_label}`,
      time: get(alert, 'events.0.time', '-'),
      actions: true,
      source: alert,
    },
  ];

  const eventsData: TableData[] = (get(alert, 'events', []) as ManualAlertEvent[]).map((event) => ({
    ...event,
    id: `event-${event.id}`,
    name,
    metric,
    direction,
    threshold,
    filter,
    sample: `${event.samples} ${metricInfo.cm_label}`,
    condition: `${convertValue(event.value, metricInfo.units)} ${metricInfo.units}`,
    source: event,
  }));

  return (
    <FormActionContext.Provider
      value={{
        formVisible: visible,
        actions: {
          onEdit,
          onDelete,
          onCancel,
        },
      }}
    >
      <Wrapper>
        <PageHeader>
          <Title>Events List for {alert.name}</Title>
          <PageButtons>
            {isAdmin && (
              <Button type="primary" onClick={onClick}>
                Edit Alert
              </Button>
            )}
            <Dropdown
              menu={{ items: [{ key: 'export', label: 'Export' }], onClick: onClickDropDown }}
              trigger={['click']}
            >
              <Space>
                <MoreOutlined />
              </Space>
            </Dropdown>
          </PageButtons>
        </PageHeader>
        <TableWrapper>
          <div style={{ height: 125 }}>
            <Table columns={columns as any} data={alertData} fixed />
          </div>
          <Table fixed columns={columns as any} data={eventsData} headerHeight={0} />
        </TableWrapper>
        <Pagination
          total={alert.totalEvents}
          pageSize={pageSize}
          defaultCurrent={page - 1}
          onChange={onClickPage}
        />
        <AlertForm
          key={JSON.stringify(editAlert)}
          alert={editAlert}
          visible={visible}
          onCancel={onCancel}
        />
        <Confirmation
          visible={confirmationVisible}
          alert={alertToDelete as ManualAlert}
          onCancel={onCancelConfirmation}
        />
      </Wrapper>
    </FormActionContext.Provider>
  );
};

export default withRouter(Events);
